import { ChangeEvent, useMemo, useState } from 'react';
import { Box, Chip, MenuItem, TextField } from '@mui/material';
import { BudgetVersionType } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import { useBudgetContext } from '../../store/Budget/BudgetContext';
import BucketSelect from './BucketSelect';
import ScenarioFormRow from './ScenarioFormRow';

interface BucketVersionFieldsProps {
    versionId: number | '';
    onChange: (versionId: number | '') => void;
    // Edit / duplicate: a scenario cannot leave its bucket, because its input rows are keyed to that
    // bucket's worktypes and HEMT locations (the backend rejects it too). The bucket is still shown —
    // locked — so the short version list reads as a rule rather than a missing entry.
    lockBucket?: boolean;
    disabled?: boolean;
}

// "Which budget": bucket first, then that bucket's versions — the same two-step choice the Budget
// pages' header offers, so a budget is picked the same way everywhere. Used by the scenario form, the
// duplicate dialog and the consolidated view.
//
// Reuses BucketSelect (in its required, no-"All buckets" form mode). It cannot reuse
// BudgetVersionSelector: that one owns the page-wide version selection plus rename/publish/export
// actions, whereas the version here is a value the caller controls.
const BucketVersionFields = ({
    versionId,
    onChange,
    lockBucket = false,
    disabled = false,
}: BucketVersionFieldsProps) => {
    const { t } = useTranslation('budget');
    const { buckets, versions, selectedBucketId } = useBudgetContext();

    // With a version chosen, the bucket IS that version's bucket — deriving it rather than storing a
    // second copy means the two fields can never disagree. The pending pick only carries the step
    // between choosing a bucket and choosing a version in it.
    const versionBucketId = versions.find((version) => version.id === versionId)?.budgetBucketId;
    const [pendingBucketId, setPendingBucketId] = useState<number | undefined>(
        // Start from the bucket the user was last looking at on the Budget pages (undefined there means
        // "All buckets", which is not a choice a scenario can have); with a single bucket there is
        // nothing to choose anyway.
        () => selectedBucketId ?? (buckets.length === 1 ? buckets[0].id : undefined)
    );
    const bucketId = versionBucketId ?? pendingBucketId;

    const versionOptions = useMemo(
        () => (bucketId == null ? [] : versions.filter((version) => version.budgetBucketId === bucketId)),
        [versions, bucketId]
    );

    const handleBucketChange = (nextBucketId: number | undefined) => {
        if (nextBucketId === bucketId) return;
        setPendingBucketId(nextBucketId);
        // Hand over a version IN the new bucket rather than clearing the field. On the consolidated view
        // the version is the shared Budget selection, and that one is COMPUTED with a fallback to the
        // newest version of the whole list — so a cleared version resolved straight back to one in the
        // old bucket and the bucket looked like it refused to change. `versions` is newest-first, so the
        // first match is the same default the Budget header would pick. A bucket always holds at least
        // its auto-created draft (CALL-758), so this lands; '' is the unreachable-in-practice fallback.
        const next = versions.find((version) => version.budgetBucketId === nextBucketId);
        onChange(next ? next.id : '');
    };

    const handleVersionChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        onChange(value === '' ? '' : Number(value));
    };

    return (
        <ScenarioFormRow>
            <Box>
                <BucketSelect
                    buckets={buckets}
                    value={bucketId}
                    onChange={handleBucketChange}
                    allowAll={false}
                    fullWidth
                    required
                    disabled={disabled || lockBucket}
                />
            </Box>
            <Box>
                {/* A TextField (not a bare Select) so the field carries the same label association,
                    sizing and helper-text behaviour as the rest of the form's inputs. */}
                <TextField
                    select
                    fullWidth
                    size="small"
                    required
                    label={t('field.budget')}
                    value={versionId}
                    onChange={handleVersionChange}
                    disabled={disabled || bucketId == null}
                >
                    {versionOptions.map((version) => (
                        <MenuItem key={version.id} value={version.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span className="notranslate">{version.name}</span>
                                {/* Same badge the Budget header's picker uses: a scenario may be
                                    attached to a released snapshot (it is a what-if layer over the
                                    frozen baseline), so which versions are frozen has to be visible
                                    while choosing. */}
                                {version.type === BudgetVersionType.SNAPSHOT && (
                                    <Chip
                                        label={t('version.statusReleased')}
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </MenuItem>
                    ))}
                </TextField>
            </Box>
        </ScenarioFormRow>
    );
};

export default BucketVersionFields;
